import type { DomainOffersEntry } from '@/pages/useCalculateOffers';
import type { CheckoutPassSelectionDto } from '@/services/orders.service';
import { isValidBirthDate } from '@/utils/booking';
import { getPassQuantity } from '@/utils/pass-quantity-key';

export type SkierSlot = {
  index: number;
  ageCategory: string;
  passTitle: string;
  domain: { id: number; name: string };
  passOfferId?: number;
  birthDate?: string;
};

export type SkierFormData = {
  skipassId?: number;
  firstName: string;
  lastName: string;
  birthDate: string;
};

export function expandPassSelections(selections: CheckoutPassSelectionDto[]): SkierSlot[] {
  const slots: SkierSlot[] = [];
  let index = 0;

  for (const sel of selections) {
    const qty = Math.max(0, sel.quantity);
    for (let i = 0; i < qty; i++) {
      slots.push({
        index,
        ageCategory: sel.ageCategory,
        passTitle: sel.passTitle ?? `${sel.ageCategory} Pass`,
        domain: sel.domain,
        passOfferId: sel.passOfferId,
      });
      index += 1;
    }
  }

  return slots;
}

export function expandSelectedOffers(
  domains: DomainOffersEntry[],
  selectedQuantities: Record<string, number>
): SkierSlot[] {
  const slots: SkierSlot[] = [];
  let index = 0;

  for (const { domain, offers } of domains) {
    for (let itemIndex = 0; itemIndex < offers.items.length; itemIndex += 1) {
      const item = offers.items[itemIndex];
      if (!item) continue;
      if (item.type !== 'pass') continue;

      const customerType = item.customer_type?.trim() ?? '';
      if (!customerType) continue;

      const qty = Math.max(0, getPassQuantity(selectedQuantities, domain.pk, item, itemIndex));
      for (let i = 0; i < qty; i += 1) {
        slots.push({
          index,
          ageCategory: customerType,
          passTitle: item.title ?? `${customerType} Pass`,
          domain: { id: domain.pk, name: domain.title },
          passOfferId: item.pass_pk,
        });
        index += 1;
      }
    }
  }

  return slots;
}

function normalizeText(value: string | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

function normalizeBirthDateForMatch(value: string | undefined): string {
  const s = (value ?? '').trim().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '';
}

function preferSlotTitle(slotTitle: string, matchedTitle: string): string {
  const slot = slotTitle.trim();
  const matched = matchedTitle.trim();
  if (!slot) return matched;
  const isGeneric = (t: string) =>
    !t || /^\s*pass\s*$/i.test(t) || /^[a-z]+\s+pass\s*$/i.test(t);
  if (isGeneric(matched) || slot.length > matched.length) return slot;
  return matched;
}

export function applyCalculatedPeopleItemsToSlots(
  baseSlots: SkierSlot[],
  domains: DomainOffersEntry[]
): SkierSlot[] {
  if (baseSlots.length === 0 || domains.length === 0) {
    return baseSlots;
  }

  type QueueEntry = {
    birthDate?: string;
    ageCategory: string;
    passTitle: string;
    slotIndex: number;
  };

  const byDomain = new Map<number, QueueEntry[]>();

  for (const { domain, offers } of domains) {
    const people = Array.isArray(offers.people) ? offers.people : [];
    const queue: QueueEntry[] = [];
    let slotIndex = 0;

    for (const person of people) {
      const items = Array.isArray(person.items) ? person.items : [];
      const personBirthDate =
        typeof person.birthDate === 'string' && person.birthDate.trim().length > 0
          ? person.birthDate.trim()
          : undefined;
      if (items.length === 0) {
        queue.push({
          birthDate: personBirthDate,
          ageCategory: 'Adult',
          passTitle: 'Pass',
          slotIndex,
        });
        slotIndex += 1;
      } else {
        for (const item of items) {
          if (item.type !== 'pass') continue;
          const customerType = item.customer_type?.trim() ?? '';
          if (!customerType) continue;
          const qty = Math.max(0, Math.trunc(item.quantity));
          for (let i = 0; i < qty; i += 1) {
            queue.push({
              birthDate: personBirthDate,
              ageCategory: customerType,
              passTitle: item.title ?? `${customerType} Pass`,
              slotIndex,
            });
            slotIndex += 1;
          }
        }
      }
    }

    if (queue.length > 0) {
      byDomain.set(domain.pk, queue);
    }
  }

  if (byDomain.size === 0) {
    return baseSlots;
  }

  const domainSlotCounters = new Map<number, number>();

  return baseSlots.map((slot, index) => {
    const queue = byDomain.get(slot.domain.id);
    const localSlotIndex = domainSlotCounters.get(slot.domain.id) ?? 0;
    domainSlotCounters.set(slot.domain.id, localSlotIndex + 1);

    if (!queue || queue.length === 0) {
      return { ...slot, index };
    }

    const slotBirthDate =
      typeof slot.birthDate === 'string' && slot.birthDate.trim().length > 0
        ? slot.birthDate.trim()
        : undefined;

    if (slotBirthDate) {
      const slotNorm = normalizeBirthDateForMatch(slotBirthDate);
      const byDobIndex =
        slotNorm.length > 0
          ? queue.findIndex((entry) => normalizeBirthDateForMatch(entry.birthDate) === slotNorm)
          : -1;

      if (byDobIndex >= 0) {
        const byDobMatched = queue.splice(byDobIndex, 1)[0] ?? null;
        if (!byDobMatched) {
          return { ...slot, index };
        }
        return {
          ...slot,
          ageCategory: byDobMatched.ageCategory,
          passTitle: preferSlotTitle(slot.passTitle, byDobMatched.passTitle),
          index,
        };
      }
      const bySlotIdx = queue.findIndex((e) => e.slotIndex === localSlotIndex);
      if (bySlotIdx >= 0) {
        const idxMatched = queue.splice(bySlotIdx, 1)[0];
        if (idxMatched) {
          return {
            ...slot,
            ageCategory: idxMatched.ageCategory,
            passTitle: preferSlotTitle(slot.passTitle, idxMatched.passTitle),
            index,
          };
        }
      }
    }

    const normalizedSlotCategory = normalizeText(slot.ageCategory);
    const bySlotIdx = queue.findIndex((e) => e.slotIndex === localSlotIndex);
    const preferredIndex =
      bySlotIdx >= 0 && queue[bySlotIdx] && normalizeText(queue[bySlotIdx].ageCategory).includes(normalizedSlotCategory)
        ? bySlotIdx
        : queue.findIndex((entry) => {
            const normalizedEntry = normalizeText(entry.ageCategory);
            return (
              normalizedEntry.includes(normalizedSlotCategory) ||
              normalizedSlotCategory.includes(normalizedEntry)
            );
          });

    const matched =
      preferredIndex >= 0
        ? (queue.splice(preferredIndex, 1)[0] ?? null)
        : (queue.shift() ?? null);

    if (!matched) {
      return { ...slot, index };
    }

    return {
      ...slot,
      ageCategory: matched.ageCategory,
      passTitle: preferSlotTitle(slot.passTitle, matched.passTitle),
      index,
    };
  });
}

export function reconcileSlotsWithCalculatedOffers(
  baseSlots: SkierSlot[],
  domains: DomainOffersEntry[]
): SkierSlot[] {
  if (baseSlots.length === 0 || domains.length === 0) {
    return baseSlots;
  }

  const offersByDomain = new Map<
    number,
    Array<{
      passOfferId?: number;
      passTitle: string;
      ageCategory: string;
      remaining: number;
    }>
  >();

  for (const { domain, offers } of domains) {
    offersByDomain.set(
      domain.pk,
      offers.items
        .filter((item) => item.type === 'pass' && (item.customer_type?.trim() ?? '').length > 0)
        .map((item) => ({
          passOfferId: item.pass_pk,
          passTitle: item.title ?? `${item.customer_type?.trim()} Pass`,
          ageCategory: item.customer_type?.trim() ?? '',
          remaining: Math.max(0, Math.trunc(item.quantity)),
        }))
        .filter((item) => item.remaining > 0)
    );
  }

  const reconciled = baseSlots.map((slot) => ({ ...slot }));
  for (const [i, slot] of reconciled.entries()) {
    const pool = offersByDomain.get(slot.domain.id);
    if (!pool || pool.length === 0) continue;

    const normalizedSlotCategory = normalizeText(slot.ageCategory);

    const match =
      pool.find((entry) => {
        if (entry.remaining <= 0) return false;
        if (entry.passOfferId == null || slot.passOfferId == null) return false;
        if (entry.passOfferId !== slot.passOfferId) return false;
        const normalizedEntryCategory = normalizeText(entry.ageCategory);
        return (
          normalizedEntryCategory.includes(normalizedSlotCategory) ||
          normalizedSlotCategory.includes(normalizedEntryCategory)
        );
      }) ??
      pool.find((entry) => {
        if (entry.remaining <= 0) return false;
        const normalizedEntryCategory = normalizeText(entry.ageCategory);
        return (
          normalizedEntryCategory.includes(normalizedSlotCategory) ||
          normalizedSlotCategory.includes(normalizedEntryCategory)
        );
      });
    if (!match) continue;

    match.remaining -= 1;
    slot.passOfferId = match.passOfferId;
    slot.passTitle = preferSlotTitle(slot.passTitle, match.passTitle);
    slot.ageCategory = match.ageCategory;
    reconciled[i] = slot;
  }

  return reconciled.map((slot, index) => ({
    ...slot,
    index,
  }));
}

export function getSlotKey(slot: SkierSlot): string {
  return `${String(slot.domain.id)}|${String(slot.passOfferId ?? '')}|${slot.ageCategory}|${slot.passTitle}`;
}

export function syncSkierSlots(
  previousSlots: SkierSlot[],
  targetSlots: SkierSlot[],
  skierForms: SkierFormData[]
): SkierSlot[] {
  if (targetSlots.length === 0) {
    return [];
  }

  const remainingByKey = new Map<string, SkierSlot[]>();
  for (const slot of targetSlots) {
    const key = getSlotKey(slot);
    const existing = remainingByKey.get(key);
    if (existing) {
      existing.push(slot);
    } else {
      remainingByKey.set(key, [slot]);
    }
  }

  const takeSlotByKey = (slot: SkierSlot | undefined): SkierSlot | null => {
    if (!slot) return null;
    const key = getSlotKey(slot);
    const queue = remainingByKey.get(key);
    if (!queue || queue.length === 0) return null;
    const taken = queue.shift() ?? null;
    if (queue.length === 0) {
      remainingByKey.delete(key);
    }
    return taken;
  };

  const consumeSlotFromPool = (slot: SkierSlot): void => {
    const key = getSlotKey(slot);
    const queue = remainingByKey.get(key);
    if (!queue) return;
    const idx = queue.indexOf(slot);
    if (idx >= 0) {
      queue.splice(idx, 1);
      if (queue.length === 0) remainingByKey.delete(key);
    }
  };

  const next: Array<SkierSlot | null> = new Array(targetSlots.length).fill(null);

  for (let i = 0; i < targetSlots.length; i += 1) {
    if (!isValidBirthDate(skierForms[i]?.birthDate ?? '')) continue;
    const slot = targetSlots[i];
    if (slot) {
      next[i] = slot;
      consumeSlotFromPool(slot);
    }
  }

  for (let i = 0; i < targetSlots.length; i += 1) {
    if (next[i]) continue;

    const fromPrev = takeSlotByKey(previousSlots[i]);
    if (fromPrev) {
      next[i] = fromPrev;
    }
  }

  const leftovers: SkierSlot[] = [];
  for (const queue of remainingByKey.values()) {
    leftovers.push(...queue);
  }

  for (let i = 0; i < targetSlots.length; i += 1) {
    if (next[i]) continue;
    const fromLeftovers = leftovers.shift();
    next[i] = fromLeftovers ?? targetSlots[i] ?? null;
  }

  return next
    .filter((slot): slot is SkierSlot => slot != null)
    .slice(0, targetSlots.length)
    .map((slot, index) => ({
      ...slot,
      index,
    }));
}
